/**
 * RatingBadge molecule — the GPR / score pill.
 *
 * Renders through the Badge atom's `TonePill` (tone="success"), whose pairing
 * is the closed `rating.*` annex (SE-6522): `rating-positive-surface` bg +
 * `rating-positive-content-strong` text — the ratified names for the
 * `success/success50` + `success/success950` variable positions the v7 design
 * binds (the scale inverts across themes so the pairing is theme-safe).
 * `label` renders a "LABEL: value" prefix (e.g. the active sort metric);
 * omitted → a fixed-width value pill.
 *
 * Data-driven seam: a future `tone`/`value+scaleMax` prop can drive a red→green
 * scale via getIndicatorColorFromScale (data-driven color → an atom `color`
 * prop, NOT a token). Kept fixed here to avoid an inline-style waiver.
 * @module components/molecules/RatingBadge/RatingBadgeView
 */
import React, { memo } from "react";

import { TonePill } from "@/components/atoms/Badge";

/** Props for RatingBadgeView */
interface RatingBadgeViewProps {
  /** Score to display */
  readonly value: number | string;
  /** Optional label prefix (e.g. "CAR"); omitted → fixed-width value pill */
  readonly label?: string | null;
  /** Optional testID */
  readonly testID?: string;
}

/**
 * Renders the score pill.
 * @param root0 - Component props
 * @param root0.value - Score to display
 * @param root0.label - Optional label prefix
 * @param root0.testID - Optional testID
 */
const RatingBadgeView = ({ value, label, testID }: RatingBadgeViewProps) => (
  <TonePill
    tone="success"
    testID={testID}
    className={`h-8 ${label ? "px-2" : "w-10"}`}
    label={label ? `${label}: ${value}` : String(value)}
  />
);

RatingBadgeView.displayName = "RatingBadgeView";

export default memo(RatingBadgeView);
